import { Link } from '@reach/router';
import React from 'react';
import { Collection } from 'shopify-storefront-api-typings';
import styled, { css } from 'styled-components';
import { withQuery } from '../data/withQuery';
import { Metafields, Product, ProductTypes, ProductWithColorImage } from '@/types/ecommerce.types';
import {
  getCurrentVariant,
  getMetafieldImage,
  getMetafieldV2,
  getUrlByColor,
  handleizeProductType,
  mapProductEdges,
} from '@/utils/utils';
import { ProductOptionsTitle } from './ProductOptions.styles';
import { colors } from '@/themes/colorsMapping';
import getProductDescription from '@/utils/getProductDescription';
import { getPartner } from '@/utils/partner';
import { useGalleryCornerImages } from '@/hooks/useGalleryCornerImages';

const StyledLink = styled(Link)<{ $availableToSell?: boolean }>`
  ${(props) =>
    !props.$availableToSell &&
    css`
      opacity: 0.4;
      cursor: not-allowed;
      display: block;
    `}
  span {
    padding: 5px 10px;
    display: inline-block;
    font-size: 14px;
    line-height: 16px;
    font-weight: 500;
  }
`;
StyledLink.displayName = 'ColorSwatch';

const SwatchesList = styled.ul`
  padding: 3px;
  margin-top: 13px;
  position: relative;
  display: grid;
  list-style-type: none;
  grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
  gap: 5px;

  @media (min-width: 800px) {
    margin-top: 10px;
    grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
  }

  li {
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid ${colors.darkGray};
    &.tmplt-product__connected-collection-item--current {
      border-color: var(--color-primary);
      box-shadow: 0 0 0 3px var(--color-primary-20);
    }
  }
  a {
    background-color: transparent !important;
    background: none !important;
    display: flex;
    flex-direction: column;
    gap: 0;
    span {
      @media (max-width: 800px) {
        display: block;
        margin-top: -10px;
      }
    }
  }
  img {
    width: 100%;
    height: 70px;
    object-position: bottom;
    object-fit: cover;
  }
  .placeholder-img {
    height: 70px;
    width: 100%;
    background-color: #f1f0ec;
    background: linear-gradient(to right, #f1f0ec 0%, #ffffff 50%, #f1f0ec 100%);
    background-size: 800px 100%;
    animation: shimmer 1.5s infinite linear;
  }
  @keyframes shimmer {
    0% {
      background-position: -800px 0;
    }
    100% {
      background-position: 800px 0;
    }
  }
`;

export type Color = {
  title: string;
  handle: string;
  color: string;
  colorName: string;
  availableToSell: boolean;
};

type PropTypes = {
  product: Product;
  colorCollection: Collection;
  className: string;
  pagePath?: string;
  showDefaultColor?: boolean;
  openStyle?: boolean;
  sectionTitle?: string;
  shopMetafields: Metafields;
};

const ProductImageSwatches = ({
  product,
  colorCollection,
  className,
  pagePath = '/products',
  showDefaultColor,
  openStyle,
  sectionTitle,
  shopMetafields,
}: PropTypes) => {
  const variants = product?.variants?.edges;
  const currentVariant = getCurrentVariant(variants);
  const productType = handleizeProductType(product?.productType);
  const frameSize = currentVariant?.selectedOptions[0]?.value;
  const partner = getPartner();
  const colors: Color[] = mapProductEdges(colorCollection);
  const isGallery = productType === ProductTypes.GALLERY;

  const tileHandles = React.useMemo(() => {
    const handles = (
      colorCollection?.products?.edges?.map((edge) => {
        const tileValue = getMetafieldV2('tile', (edge.node.metafields as any) || []);
        if (!tileValue) return null;
        try {
          const parsed = JSON.parse(tileValue);
          if (Array.isArray(parsed) && parsed.length > 0) {
            return parsed[0]?.split(':')?.[0];
          }
        } catch (e) {
          console.error('Error parsing tile value:', e);
        }
        return null;
      }) || []
    ).filter(Boolean) as string[];

    return Array.from(new Set(handles));
  }, [colorCollection]);

  const cornerImages = useGalleryCornerImages(tileHandles);
  const images =
    colorCollection?.products?.edges?.map((edge) =>
      getMetafieldImage((edge.node as ProductWithColorImage).color_image, edge.node.images.edges)
    ) || [];
  const { color: productColor } = getProductDescription(product, shopMetafields);

  if (colors.length <= 1 || !showDefaultColor) {
    return null;
  }

  if (
    !product ||
    !currentVariant ||
    !colorCollection ||
    (productType !== ProductTypes.PICTURE &&
      productType !== ProductTypes.GALLERY &&
      productType !== ProductTypes.FRAMEDCANVAS &&
      productType !== ProductTypes.BLACKLABEL &&
      !showDefaultColor)
  ) {
    return null;
  }

  return (
    <div className={className}>
      {sectionTitle && (
        <ProductOptionsTitle>
          {sectionTitle}: {'\u00A0'}
          <span>{productColor}</span>
        </ProductOptionsTitle>
      )}
      <SwatchesList>
        {colors?.map((color, index) => {
          const cornerImageObj = cornerImages?.find(
            (img) => img.colorName.toLowerCase() === color.colorName.toLowerCase()
          );

          const imageUrl = isGallery
            ? cornerImageObj?.image ?? cornerImages?.[index]?.image ?? images[index] ?? ''
            : images[index] ?? '';
          const url = getUrlByColor({
            pagePath,
            color: color.handle,
            productType,
            frameSize,
            openStyle,
            partner,
          });

          return (
            <li
              key={color.handle}
              className={`product-option-color-item ${
                product.handle === color.handle
                  ? 'tmplt-product__connected-collection-item--current'
                  : ''
              }`}
            >
              <StyledLink
                $availableToSell={color.availableToSell}
                to={url}
                onClick={(e) => {
                  if (!color.availableToSell) e.preventDefault();
                }}
                className={`color-${color?.colorName.toLowerCase()}${
                  !color?.availableToSell ? ' tmplt-product__variants-item--sold-out' : ''
                }`}
                style={{ backgroundColor: color?.color }}
                title={color.title}
              >
                {imageUrl ? <img src={imageUrl} alt="" /> : <div className="placeholder-img"></div>}
                <span>{color.colorName}</span>
              </StyledLink>
            </li>
          );
        })}
      </SwatchesList>
    </div>
  );
};

ProductImageSwatches.displayName = 'ProductImageSwatches';
export default withQuery(ProductImageSwatches);
